/**
 * Draft hydration — tolerant deep-merge of a SERVER draft payload over the
 * wizard defaults (PR-ONB-RESUME-1B).
 *
 * The autosaved payload is our own data but still treated defensively
 * (schema evolves; the BE stores it verbatim): unknown keys are dropped,
 * type mismatches keep the default, arrays/primitives are replaced whole.
 * The result always has the full RegistrationFormValues shape so every
 * controlled input keeps a defined value after form.reset().
 */

export function hydrateDraftOverDefaults<T extends Record<string, unknown>>(
  defaults: T,
  payload: Record<string, unknown> | null | undefined,
): T {
  if (!payload) return defaults;
  return mergeNode(defaults, payload) as T;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  );
}

function mergeNode(
  defaults: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (!(key in incoming)) continue;
    const def = defaults[key];
    const inc = incoming[key];
    if (isPlainObject(def)) {
      if (isPlainObject(inc)) out[key] = mergeNode(def, inc);
      continue; // mismatched shape → keep default
    }
    if (Array.isArray(def)) {
      if (Array.isArray(inc)) out[key] = inc;
      continue;
    }
    // Primitive slot: accept same-type primitives; allow string into
    // empty-string-typed enums (the form uses '' sentinels).
    if (inc === null || inc === undefined) continue;
    if (typeof inc === typeof def) out[key] = inc;
  }
  return out;
}
